import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowUpRight, ArrowDownRight, ArrowRightLeft, Send, Download,
  Wallet, DollarSign, Loader2, QrCode, ShoppingCart, TrendingUp
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientCryptoPortfolio() {
  const { token } = useAuth();
  const { primaryColor } = useTheme();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [allPrices, setAllPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState({});

  // Dialogs
  const [swapDialog, setSwapDialog] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [sellDialog, setSellDialog] = useState(false);
  const [receiveDialog, setReceiveDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selected, setSelected] = useState(null);

  // Chart
  const [chartData, setChartData] = useState([]);
  const [chartDays, setChartDays] = useState(7);
  const [loadingChart, setLoadingChart] = useState(false);

  // Swap
  const [swapTo, setSwapTo] = useState('');
  const [swapQty, setSwapQty] = useState('');
  const [swapping, setSwapping] = useState(false);
  const [swapProcessing, setSwapProcessing] = useState(false);

  // Buy
  const [buyDialog, setBuyDialog] = useState(false);
  const [buyAmount, setBuyAmount] = useState('');
  const [buyMode, setBuyMode] = useState('eur');
  const [buying, setBuying] = useState(false);
  const [availableFunds, setAvailableFunds] = useState(0);

  // Send
  const [sendAddress, setSendAddress] = useState('');
  const [sendQty, setSendQty] = useState('');
  const [sendMode, setSendMode] = useState('crypto');
  const [sending, setSending] = useState(false);

  // Sell
  const [sellQty, setSellQty] = useState('');
  const [sellMode, setSellMode] = useState('eur'); // 'eur' or 'crypto'
  const [selling, setSelling] = useState(false);

  const [livePrices, setLivePrices] = useState({});

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 60000); return () => clearInterval(i); }, [token]);

  // Live price ticker - simulates micro-fluctuations every 2s
  useEffect(() => {
    if (!portfolio?.holdings?.length) return;
    const tick = setInterval(() => {
      setLivePrices(prev => {
        const next = { ...prev };
        (portfolio?.holdings || []).forEach(h => {
          const base = h.current_price || 0;
          if (base <= 0) return;
          const existing = prev[h.symbol] || base;
          // Random walk: ±0.05% per tick for realism
          const jitter = existing * (Math.random() - 0.5) * 0.001;
          next[h.symbol] = existing + jitter;
        });
        return next;
      });
    }, 2500);
    return () => clearInterval(tick);
  }, [portfolio]);

  const getLivePrice = (symbol, fallback) => livePrices[symbol] || fallback || 0;

  const fetchData = async () => {
    try {
      const [portRes, pricesRes, walletsRes, fundsRes] = await Promise.all([
        axios.get(`${API}/crypto/portfolio`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crypto/prices`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crypto/wallet-status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/portfolios`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setPortfolio(portRes.data);
      setAllPrices(pricesRes.data || []);
      setWallets(walletsRes.data || {});
      setAvailableFunds((fundsRes.data || []).filter(p => p.portfolio_type === 'available').reduce((s, p) => s + (p.available || 0), 0));
      // Initialize live prices from API data
      const p = {};
      (portRes.data?.holdings || []).forEach(h => { if (h.current_price > 0) p[h.symbol] = h.current_price; });
      setLivePrices(prev => {
        // Only update if we have new real prices, keep existing if API returned 0
        const merged = { ...prev };
        Object.entries(p).forEach(([k, v]) => { if (v > 0) merged[k] = v; });
        return merged;
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchChart = async (coinId, days) => {
    if (!coinId) return;
    setLoadingChart(true);
    try {
      const res = await axios.get(`${API}/crypto/chart/${coinId}?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      setChartData((res.data?.prices || []).map(([ts, price]) => ({
        time: new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: days <= 1 ? '2-digit' : undefined, minute: days <= 1 ? '2-digit' : undefined }),
        price,
      })));
    } catch { setChartData([]); }
    finally { setLoadingChart(false); }
  };

  const fmt = (v, d = 2) => v?.toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }) || '0';
  const fmtQty = (q) => q < 0.01 ? q.toFixed(8) : q < 1 ? q.toFixed(6) : q.toFixed(4);

  // DETAIL
  const openDetail = (h) => {
    setSelected(h); setChartDays(7); setDetailDialog(true);
    fetchChart(h.coin_id, 7);
  };

  // SWAP
  const openSwap = (h) => { setSelected(h); setSwapTo(''); setSwapQty(''); setSwapProcessing(false); setSwapDialog(true); };
  const swapToPrice = allPrices.find(p => p.symbol === swapTo)?.current_price || 0;
  const swapEur = selected && swapQty ? parseFloat(swapQty) * (selected.current_price || 0) : 0;
  const swapFee = swapEur * 0.005;
  const swapReceive = swapToPrice > 0 ? (swapEur - swapFee) / swapToPrice : 0;

  const handleSwap = async () => {
    if (!parseFloat(swapQty) || !swapTo) return;
    if (parseFloat(swapQty) > selected.quantity) { toast.error('Quantité insuffisante'); return; }
    setSwapping(true); setSwapProcessing(true);
    await new Promise(r => setTimeout(r, 3000));
    try {
      await axios.post(`${API}/crypto/swap`, { from_symbol: selected.symbol, to_symbol: swapTo, from_quantity: parseFloat(swapQty) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Swap effectué !`);
      setSwapDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSwapping(false); setSwapProcessing(false); }
  };

  // SEND
  const openSend = (h) => { setSelected(h); setSendAddress(''); setSendQty(''); setSendMode('crypto'); setSendDialog(true); };
  const sendCryptoQty = sendMode === 'eur' && selected ? (parseFloat(sendQty) || 0) / (selected.current_price || 1) : parseFloat(sendQty) || 0;

  const handleSend = async () => {
    if (!sendAddress || sendCryptoQty <= 0) return;
    if (sendCryptoQty > selected.quantity) { toast.error('Quantité insuffisante'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/crypto/send`, { symbol: selected.symbol, quantity: sendCryptoQty, wallet_address: sendAddress }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Envoi effectué !'); setSendDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSending(false); }
  };

  // SELL
  const openSell = (h) => { setSelected(h); setSellQty(''); setSellMode('eur'); setSellDialog(true); };
  const sellCryptoQty = sellMode === 'eur' && selected ? (parseFloat(sellQty) || 0) / (getLivePrice(selected.symbol, selected.current_price) || 1) : parseFloat(sellQty) || 0;
  const sellEurValue = sellMode === 'crypto' && selected ? (parseFloat(sellQty) || 0) * (getLivePrice(selected.symbol, selected.current_price) || 0) : parseFloat(sellQty) || 0;

  const handleSell = async () => {
    if (!parseFloat(sellQty) || parseFloat(sellQty) <= 0) { toast.error('Montant invalide'); return; }
    const actualQty = sellMode === 'eur' ? sellCryptoQty : parseFloat(sellQty);
    if (actualQty > selected.quantity) { toast.error('Quantité insuffisante'); return; }
    setSelling(true);
    try {
      const payload = sellMode === 'eur'
        ? { symbol: selected.symbol, amount_eur: parseFloat(sellQty) }
        : { symbol: selected.symbol, quantity: parseFloat(sellQty) };
      const res = await axios.post(`${API}/crypto/sell`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`+${res.data.amount_eur?.toLocaleString('fr-FR')} € crédités sur vos fonds !`);
      setSellDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setSelling(false); }
  };

  // RECEIVE
  const openReceive = (h) => { setSelected(h); setReceiveDialog(true); };

  // BUY
  const openBuy = (h) => { setSelected(h); setBuyAmount(''); setBuyMode('eur'); setBuyDialog(true); };
  const buyLivePrice = selected ? getLivePrice(selected.symbol, selected.current_price) : 0;
  const buyEurAmt = buyMode === 'eur' ? (parseFloat(buyAmount) || 0) : (parseFloat(buyAmount) || 0) * buyLivePrice;
  const buyCryptoQty = buyMode === 'crypto' ? (parseFloat(buyAmount) || 0) : (buyLivePrice > 0 ? (parseFloat(buyAmount) || 0) / buyLivePrice : 0);

  const handleBuy = async () => {
    if (!buyAmount || buyEurAmt <= 0) { toast.error('Montant invalide'); return; }
    if (buyEurAmt > availableFunds) { toast.error('Fonds insuffisants'); return; }
    setBuying(true);
    try {
      const r = await axios.post(`${API}/crypto/buy`, { symbol: selected.symbol, amount_eur: buyEurAmt }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${r.data.quantity?.toFixed(8)} ${selected.symbol} acheté !`);
      setBuyDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setBuying(false); }
  };
  const getWallet = (symbol) => wallets[symbol] || null;
  const handleRequestWallet = async () => {
    try {
      await axios.post(`${API}/crypto/wallet-request`, { symbol: selected.symbol }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demande envoyée !'); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };
  const copyAddr = (a) => { navigator.clipboard.writeText(a); toast.success('Copié !'); };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="spinner" /></div></DashboardLayout>;

  const holdings = portfolio?.holdings || [];

  // Compute live totals
  const liveHoldings = holdings.map(h => {
    const lp = getLivePrice(h.symbol, h.current_price);
    const liveValue = h.quantity * lp;
    const livePnl = liveValue - (h.total_cost || 0);
    const livePnlPct = h.total_cost > 0 ? (livePnl / h.total_cost * 100) : 0;
    return { ...h, livePrice: lp, liveValue, livePnl, livePnlPct };
  });
  const liveTotalValue = liveHoldings.reduce((s, h) => s + h.liveValue, 0);
  const liveTotalInvested = liveHoldings.reduce((s, h) => s + (h.total_cost || 0), 0);
  const liveTotalPnl = liveTotalValue - liveTotalInvested;
  const liveTotalPnlPct = liveTotalInvested > 0 ? (liveTotalPnl / liveTotalInvested * 100) : 0;
  const isUpTotal = liveTotalPnl >= 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="crypto-portfolio-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Mes Cryptos</h1>
          <Button onClick={() => navigate('/client/crypto-buy')} style={{ backgroundColor: primaryColor }}>
            <ShoppingCart className="w-4 h-4 mr-2" /> Acheter
          </Button>
        </div>

        {/* Portfolio Summary */}
        <Card className="bg-gradient-to-br from-card/90 to-card/60 backdrop-blur border-border/30 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">Portefeuille Crypto</p>
                <p className="text-4xl font-bold transition-all">{fmt(liveTotalValue)} €</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`text-sm font-semibold flex items-center gap-1 ${isUpTotal ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isUpTotal ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {isUpTotal ? '+' : ''}{fmt(liveTotalPnl)} € ({liveTotalPnlPct.toFixed(2)}%)
                  </span>
                  <span className="text-xs text-muted-foreground">Investi: {fmt(liveTotalInvested)} €</span>
                  <span className="text-[10px] text-muted-foreground animate-pulse">EN DIRECT</span>
                </div>
              </div>
              <div className="flex gap-6 text-center">
                <div>
                  <p className="text-2xl font-bold">{holdings.length}</p>
                  <p className="text-[10px] text-muted-foreground uppercase">Actifs</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {holdings.length === 0 ? (
          <Card className="bg-card/80 border-border/30">
            <CardContent className="py-16 text-center">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-lg text-muted-foreground mb-4">Aucune crypto dans votre portefeuille</p>
              <Button onClick={() => navigate('/client/crypto-buy')} style={{ backgroundColor: primaryColor }}>
                Acheter des Cryptos
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {liveHoldings.map(h => {
              const isUp = h.livePnl >= 0;
              const pct24 = h.price_change_24h || 0;
              const isUp24 = pct24 >= 0;
              const alloc = liveTotalValue > 0 ? ((h.liveValue / liveTotalValue) * 100).toFixed(1) : 0;
              return (
                <Card key={h.symbol} className="bg-card/80 backdrop-blur border-border/30 hover:border-primary/30 transition-all cursor-pointer group" onClick={() => openDetail(h)} data-testid={`holding-${h.symbol}`}>
                  <CardContent className="p-0">
                    <div className="flex items-center gap-0">
                      {/* Left - coin info */}
                      <div className="flex items-center gap-4 p-5 flex-1 min-w-0">
                        <div className="relative">
                          {h.image && <img src={h.image} alt={h.symbol} className="w-12 h-12 rounded-full" onError={e => { e.target.style.display = 'none'; }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-bold text-lg">{h.symbol}</p>
                            <p className="text-xs text-muted-foreground truncate">{h.name}</p>
                          </div>
                          <div className="flex items-center gap-4 mt-0.5">
                            <span className="text-sm font-mono text-muted-foreground">{fmtQty(h.quantity)} {h.symbol}</span>
                            <span className="text-xs text-muted-foreground">{alloc}%</span>
                          </div>
                        </div>
                      </div>
                      {/* Mid - cours actuel */}
                      <div className="text-right px-5 hidden md:block min-w-[130px]">
                        <p className="text-[10px] text-muted-foreground uppercase">Cours</p>
                        <p className="font-mono text-sm transition-all">{fmt(h.livePrice)} €</p>
                        <span className={`text-xs ${isUp24 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isUp24 ? '+' : ''}{pct24.toFixed(2)}%
                        </span>
                      </div>
                      {/* Right - valeur position */}
                      <div className="text-right p-5 border-l border-border/20 min-w-[150px]">
                        <p className="text-[10px] text-muted-foreground uppercase">Ma position</p>
                        <p className="font-bold text-lg transition-all">{fmt(h.liveValue)} €</p>
                        <span className={`text-sm font-semibold ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isUp ? '+' : ''}{fmt(h.livePnl)} €
                        </span>
                      </div>
                      {/* Actions - labeled buttons */}
                      <div className="flex items-center gap-1 p-3 border-l border-border/20" onClick={e => e.stopPropagation()}>
                        <Button size="sm" variant="outline" className="text-[10px] h-7 px-2 gap-1" onClick={() => openBuy(h)} title="Acheter"><ShoppingCart className="w-3 h-3" /><span className="hidden lg:inline">Acheter</span></Button>
                        <Button size="sm" variant="outline" className="text-[10px] h-7 px-2 gap-1" onClick={() => openSwap(h)} title="Swap"><ArrowRightLeft className="w-3 h-3" /><span className="hidden lg:inline">Swap</span></Button>
                        <Button size="sm" variant="outline" className="text-[10px] h-7 px-2 gap-1" onClick={() => openSend(h)} title="Envoyer"><Send className="w-3 h-3" /><span className="hidden lg:inline">Envoyer</span></Button>
                        <Button size="sm" variant="outline" className="text-[10px] h-7 px-2 gap-1 text-red-400 hover:text-red-300" onClick={() => openSell(h)} title="Vendre"><DollarSign className="w-3 h-3" /><span className="hidden lg:inline">Vendre</span></Button>
                        <Button size="sm" variant="outline" className="text-[10px] h-7 px-2 gap-1" onClick={() => openReceive(h)} title="Recevoir"><Download className="w-3 h-3" /><span className="hidden lg:inline">Recevoir</span></Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* ===== DETAIL DIALOG ===== */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="detail-dialog">
            {selected && (() => {
              const lp = getLivePrice(selected.symbol, selected.current_price);
              const liveVal = selected.quantity * lp;
              const livePnl = liveVal - (selected.total_cost || 0);
              const livePnlPct = selected.total_cost > 0 ? (livePnl / selected.total_cost * 100) : 0;
              const isUp = livePnl >= 0;
              const coinId = selected.coin_id;
              const coinInfo = allPrices.find(p => p.symbol === selected.symbol) || {};
              const pct24 = coinInfo.price_change_percentage_24h || 0;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                      {selected.image && <img src={selected.image} alt="" className="w-10 h-10 rounded-full" />}
                      <div><span className="text-xl">{selected.name}</span><span className="text-sm text-muted-foreground ml-2">{selected.symbol}</span></div>
                    </DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-xl bg-secondary/30">
                      <p className="text-xs text-muted-foreground">Prix en direct</p>
                      <p className="text-2xl font-bold font-mono transition-all">{fmt(lp)} €</p>
                      <span className={`text-xs font-semibold ${pct24 >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{pct24 >= 0 ? '+' : ''}{pct24.toFixed(2)}% (24h)</span>
                    </div>
                    <div className="p-4 rounded-xl bg-secondary/30">
                      <p className="text-xs text-muted-foreground">Votre position</p>
                      <p className="text-2xl font-bold transition-all">{fmt(liveVal)} €</p>
                      <span className={`text-xs font-semibold ${isUp ? 'text-emerald-400' : 'text-red-400'}`}>P&L: {isUp ? '+' : ''}{fmt(livePnl)} € ({livePnlPct.toFixed(2)}%)</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {[
                      { l: 'Quantité', v: fmtQty(selected.quantity) },
                      { l: 'Investi', v: `${fmt(selected.total_cost)} €` },
                      { l: 'Prix moyen', v: `${fmt(selected.total_cost / selected.quantity)} €` },
                      { l: 'Cap.', v: coinInfo.market_cap ? `${(coinInfo.market_cap / 1e9).toFixed(1)} Mrd` : '-' },
                    ].map(s => (
                      <div key={s.l} className="p-2.5 rounded-lg bg-secondary/20"><p className="text-[10px] text-muted-foreground">{s.l}</p><p className="text-sm font-bold font-mono">{s.v}</p></div>
                    ))}
                  </div>
                  {/* Chart */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold">Historique</p>
                      <div className="flex gap-1">{[{d:1,l:'24h'},{d:7,l:'7j'},{d:30,l:'30j'},{d:90,l:'90j'},{d:365,l:'1an'}].map(o => (
                        <Button key={o.d} size="sm" variant={chartDays===o.d?'default':'ghost'} className="text-xs h-6 px-2" onClick={() => { setChartDays(o.d); fetchChart(coinId, o.d); }}>{o.l}</Button>
                      ))}</div>
                    </div>
                    <div className="h-48 w-full rounded-lg overflow-hidden bg-secondary/10">
                      {loadingChart ? (
                        <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
                      ) : chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={chartData}>
                            <defs><linearGradient id="dGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={primaryColor||'#D4AF37'} stopOpacity={0.4}/><stop offset="95%" stopColor={primaryColor||'#D4AF37'} stopOpacity={0}/></linearGradient></defs>
                            <XAxis dataKey="time" hide /><YAxis domain={['auto','auto']} hide />
                            <Tooltip contentStyle={{background:'#1a1a2e',border:'1px solid #333',borderRadius:8,fontSize:12}} formatter={v=>[`${v.toLocaleString('fr-FR')} €`,'Prix']}/>
                            <Area type="monotone" dataKey="price" stroke={primaryColor||'#D4AF37'} fill="url(#dGrad)" strokeWidth={2} dot={false}/>
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : <div className="flex items-center justify-center h-full text-muted-foreground text-sm"><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Chargement...</div>}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-border/30">
                    <Button size="sm" style={{backgroundColor: primaryColor}} className="flex-1 text-white" onClick={()=>{setDetailDialog(false);setTimeout(()=>openBuy(selected),200)}}><ShoppingCart className="w-4 h-4 mr-1"/>Acheter</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={()=>{setDetailDialog(false);setTimeout(()=>openSwap(selected),200)}}><ArrowRightLeft className="w-4 h-4 mr-1"/>Swap</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={()=>{setDetailDialog(false);setTimeout(()=>openSend(selected),200)}}><Send className="w-4 h-4 mr-1"/>Envoyer</Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={()=>{setDetailDialog(false);setTimeout(()=>openSell(selected),200)}}><DollarSign className="w-4 h-4 mr-1"/>Vendre</Button>
                    <Button size="sm" variant="outline" onClick={()=>{setDetailDialog(false);setTimeout(()=>openReceive(selected),200)}}><Download className="w-4 h-4"/></Button>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* ===== SWAP DIALOG ===== */}
        <Dialog open={swapDialog} onOpenChange={setSwapDialog}>
          <DialogContent className="max-w-md" data-testid="swap-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5"/>Swap {selected?.symbol}</DialogTitle></DialogHeader>
            {selected && (swapProcessing ? (
              <div className="py-8 text-center space-y-4">
                <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary"/>
                <p className="text-lg font-semibold">Swap en cours...</p>
                <p className="text-sm text-muted-foreground">Traitement sur le réseau</p>
                <div className="w-full bg-secondary rounded-full h-2"><div className="h-2 rounded-full animate-pulse" style={{backgroundColor:primaryColor,width:'75%'}}/></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-secondary/30">
                  <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2">{selected.image&&<img src={selected.image} alt="" className="w-6 h-6 rounded-full"/>}<span className="font-bold">{selected.symbol}</span></div><span className="text-xs text-muted-foreground">Max: {fmtQty(selected.quantity)}</span></div>
                  <Input type="number" step="any" value={swapQty} onChange={e=>setSwapQty(e.target.value)} placeholder="0.00" data-testid="swap-qty-input"/>
                  {swapQty && parseFloat(swapQty) > selected.quantity && (
                    <p className="text-xs text-red-400 mt-1 font-semibold">Quantité insuffisante ! Max: {fmtQty(selected.quantity)} {selected.symbol}</p>
                  )}
                  <button className="text-xs mt-1 underline" style={{color:primaryColor}} onClick={()=>setSwapQty(String(selected.quantity))}>MAX</button>
                </div>
                <div className="flex justify-center"><ArrowRightLeft className="w-5 h-5 text-muted-foreground rotate-90"/></div>
                <div className="p-3 rounded-lg bg-secondary/30">
                  <Select value={swapTo} onValueChange={setSwapTo}><SelectTrigger data-testid="swap-to-select"><SelectValue placeholder="Choisir"/></SelectTrigger>
                    <SelectContent className="max-h-60">{allPrices.filter(p=>p.symbol!==selected.symbol).map(p=>(<SelectItem key={p.symbol} value={p.symbol}><span className="flex items-center gap-2">{p.image&&<img src={p.image} alt="" className="w-4 h-4 rounded-full inline"/>}{p.symbol} - {p.name}</span></SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {swapQty&&parseFloat(swapQty)>0&&swapTo&&(
                  <div className="p-3 rounded-lg border border-primary/20 bg-primary/5 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Valeur</span><span className="font-mono">{fmt(swapEur)} €</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Frais (0.5%)</span><span className="font-mono text-yellow-400">-{swapFee.toFixed(2)} €</span></div>
                    <div className="flex justify-between font-bold border-t border-border/30 pt-1"><span>Vous recevrez</span><span className="font-mono">{swapReceive.toFixed(8)} {swapTo}</span></div>
                  </div>
                )}
              </div>
            ))}
            {!swapProcessing&&<DialogFooter><Button variant="outline" onClick={()=>setSwapDialog(false)}>Annuler</Button><Button onClick={handleSwap} disabled={swapping||!swapTo||!swapQty||parseFloat(swapQty)>selected.quantity} style={{backgroundColor:primaryColor}} data-testid="confirm-swap-btn">{swapping?'En cours...':'Confirmer le Swap'}</Button></DialogFooter>}
          </DialogContent>
        </Dialog>

        {/* ===== SEND DIALOG ===== */}
        <Dialog open={sendDialog} onOpenChange={setSendDialog}>
          <DialogContent className="max-w-md" data-testid="send-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5"/>Envoyer {selected?.symbol}</DialogTitle></DialogHeader>
            {selected&&(<div className="space-y-4">
              <div><Label>Adresse wallet</Label><div className="flex gap-2"><Input value={sendAddress} onChange={e=>setSendAddress(e.target.value)} placeholder="0x..." className="flex-1 font-mono text-xs" data-testid="send-address-input"/><Button variant="outline" size="sm" onClick={()=>toast.info('Collez l\'adresse du wallet')}><QrCode className="w-4 h-4"/></Button></div></div>
              <div><Label>Montant</Label><div className="flex gap-2 mb-2"><Button size="sm" variant={sendMode==='crypto'?'default':'outline'} onClick={()=>{setSendMode('crypto');setSendQty('')}} className="text-xs">{selected.symbol}</Button><Button size="sm" variant={sendMode==='eur'?'default':'outline'} onClick={()=>{setSendMode('eur');setSendQty('')}} className="text-xs">EUR</Button></div>
                <Input type="number" step="any" value={sendQty} onChange={e=>setSendQty(e.target.value)} placeholder="0.00" data-testid="send-qty-input"/>
                <p className="text-xs text-muted-foreground mt-1">Dispo: {fmtQty(selected.quantity)} {selected.symbol}</p>
              </div>
              {sendQty&&parseFloat(sendQty)>0&&(<div className="p-3 rounded-lg border border-primary/20 bg-primary/5 text-sm space-y-1"><div className="flex justify-between"><span className="text-muted-foreground">Envoi</span><span className="font-mono font-bold">{sendCryptoQty.toFixed(8)} {selected.symbol}</span></div></div>)}
            </div>)}
            <DialogFooter><Button variant="outline" onClick={()=>setSendDialog(false)}>Annuler</Button><Button onClick={handleSend} disabled={sending||!sendAddress||!sendQty} style={{backgroundColor:primaryColor}} data-testid="confirm-send-btn">{sending?'En cours...':'Confirmer'}</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== SELL DIALOG ===== */}
        <Dialog open={sellDialog} onOpenChange={setSellDialog}>
          <DialogContent className="max-w-md" data-testid="sell-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5"/>Vendre {selected?.symbol}</DialogTitle></DialogHeader>
            {selected&&(<div className="space-y-4">
              <div className="p-3 rounded-lg bg-secondary/30 text-center">
                <p className="text-xs text-muted-foreground">Prix actuel</p>
                <p className="text-2xl font-bold font-mono transition-all">{fmt(getLivePrice(selected.symbol, selected.current_price))} €</p>
              </div>
              <div>
                <Label>Montant à vendre</Label>
                <div className="flex gap-2 mb-2">
                  <Button size="sm" variant={sellMode === 'eur' ? 'default' : 'outline'} onClick={() => { setSellMode('eur'); setSellQty(''); }} className="text-xs">EUR €</Button>
                  <Button size="sm" variant={sellMode === 'crypto' ? 'default' : 'outline'} onClick={() => { setSellMode('crypto'); setSellQty(''); }} className="text-xs">{selected.symbol}</Button>
                </div>
                <Input type="number" step="any" value={sellQty} onChange={e => setSellQty(e.target.value)} placeholder={sellMode === 'eur' ? '100.00' : '0.00000000'} data-testid="sell-qty-input" />
                {sellQty && parseFloat(sellQty) > 0 && (sellMode === 'eur' ? sellCryptoQty : parseFloat(sellQty)) > selected.quantity && (
                  <p className="text-xs text-red-400 mt-1 font-semibold">Quantité insuffisante ! Vous avez {fmtQty(selected.quantity)} {selected.symbol}</p>
                )}
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-muted-foreground">
                    Dispo: {fmtQty(selected.quantity)} {selected.symbol} ({fmt(selected.quantity * getLivePrice(selected.symbol, selected.current_price))} €)
                  </p>
                  <button className="text-xs underline" style={{ color: primaryColor }} onClick={() => setSellQty(sellMode === 'eur' ? String(Math.floor(selected.quantity * getLivePrice(selected.symbol, selected.current_price) * 100) / 100) : String(selected.quantity))}>TOUT</button>
                </div>
              </div>
              {sellQty && parseFloat(sellQty) > 0 && (
                <div className="p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-sm space-y-1.5">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Vous vendez</span>
                    <span className="font-mono">{sellMode === 'eur' ? sellCryptoQty.toFixed(8) : parseFloat(sellQty).toFixed(8)} {selected.symbol}</span>
                  </div>
                  <div className="flex justify-between font-bold text-emerald-400 text-base">
                    <span>Vous recevrez</span>
                    <span className="font-mono">+{fmt(sellMode === 'eur' ? parseFloat(sellQty) : sellEurValue)} €</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Crédité sur vos fonds disponibles</p>
                </div>
              )}
            </div>)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setSellDialog(false)}>Annuler</Button>
              <Button onClick={handleSell} disabled={selling || !sellQty || parseFloat(sellQty) <= 0 || (sellMode === 'eur' ? sellCryptoQty : parseFloat(sellQty)) > selected.quantity} variant="destructive" data-testid="confirm-sell-btn">
                {selling ? 'Vente en cours...' : 'Confirmer la vente'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== BUY DIALOG ===== */}
        <Dialog open={buyDialog} onOpenChange={setBuyDialog}>
          <DialogContent className="max-w-md" data-testid="buy-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5" />Acheter {selected?.symbol}</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-secondary/30 text-center">
                  <p className="text-xs text-muted-foreground">Prix actuel</p>
                  <p className="text-2xl font-bold font-mono transition-all">{fmt(buyLivePrice)} €</p>
                </div>
                <div className="p-2 bg-secondary/20 rounded text-sm text-center">Fonds disponibles : <span className="font-bold">{availableFunds.toLocaleString('fr-FR')} €</span></div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Montant</Label>
                    <div className="flex gap-1">
                      <Button size="sm" variant={buyMode === 'eur' ? 'default' : 'outline'} onClick={() => { setBuyMode('eur'); setBuyAmount(''); }} className="text-xs h-7 px-3">EUR €</Button>
                      <Button size="sm" variant={buyMode === 'crypto' ? 'default' : 'outline'} onClick={() => { setBuyMode('crypto'); setBuyAmount(''); }} className="text-xs h-7 px-3">{selected.symbol}</Button>
                    </div>
                  </div>
                  <Input type="number" step="any" value={buyAmount} onChange={e => setBuyAmount(e.target.value)} placeholder={buyMode === 'eur' ? '100' : '0.001'} autoFocus data-testid="portfolio-buy-amount" />
                  {buyAmount && buyEurAmt > availableFunds && <p className="text-xs text-red-400 mt-1 font-semibold">Fonds insuffisants !</p>}
                  {buyMode === 'eur' && <div className="flex gap-1 mt-1">{[25, 50, 100, 250, 500].map(a => (<button key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/50 hover:bg-secondary" onClick={() => setBuyAmount(String(a))}>{a}€</button>))}</div>}
                </div>
                {buyAmount && parseFloat(buyAmount) > 0 && (
                  <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Vous recevrez</span><span className="font-mono font-bold text-emerald-400">{buyCryptoQty.toFixed(8)} {selected.symbol}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Coût</span><span className="font-mono">{buyEurAmt.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span></div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuyDialog(false)}>Annuler</Button>
              <Button onClick={handleBuy} disabled={buying || !buyAmount || buyEurAmt <= 0 || buyEurAmt > availableFunds} style={{ backgroundColor: primaryColor }} data-testid="confirm-portfolio-buy">
                {buying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Achat...</> : `Acheter ${selected?.symbol}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== RECEIVE DIALOG ===== */}
        <Dialog open={receiveDialog} onOpenChange={setReceiveDialog}>
          <DialogContent className="max-w-md" data-testid="receive-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5"/>Recevoir {selected?.symbol}</DialogTitle></DialogHeader>
            {selected&&(()=>{
              const w = getWallet(selected.symbol);
              if(!w) return <div className="space-y-4 text-center py-4"><Wallet className="w-12 h-12 mx-auto text-primary opacity-50"/><p className="text-sm text-muted-foreground">Pas encore de wallet {selected.symbol}.</p><Button onClick={handleRequestWallet} style={{backgroundColor:primaryColor}} className="w-full" data-testid="request-wallet-btn">Demander un wallet</Button></div>;
              if(w.status==='pending') return <div className="space-y-3 text-center py-4"><Loader2 className="w-10 h-10 mx-auto animate-spin text-yellow-400"/><Badge className="bg-yellow-500/20 text-yellow-400">En attente</Badge><p className="text-sm text-muted-foreground">Votre conseiller traite votre demande.</p></div>;
              return <div className="space-y-4"><Badge className="bg-emerald-500/20 text-emerald-400">Wallet actif</Badge>{w.qr_code_url && <div className="flex justify-center py-2"><img src={w.qr_code_url} alt="QR Code" className="w-48 h-48 bg-white rounded-lg p-2 object-contain" /></div>}<div className="p-4 rounded-lg bg-secondary/30 border border-border/50"><p className="text-xs text-muted-foreground mb-1">Adresse</p><p className="font-mono text-sm break-all select-all">{w.wallet_address}</p>{w.network&&<p className="text-xs text-muted-foreground mt-2">Réseau: {w.network}</p>}</div><Button variant="outline" className="w-full" onClick={()=>copyAddr(w.wallet_address)}>Copier l'adresse</Button></div>;
            })()}
            <DialogFooter><Button variant="outline" onClick={()=>setReceiveDialog(false)}>Fermer</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
